import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/services/api";
import type { WorkOrder } from "@/types/WorkOrder";

// ============= QUERIES =============

interface GetWorkOrdersParams {
  status?: "PENDING" | "IN_PROGRESS" | "SUSPENDED" | "COMPLETED" | "CANCELLED";
  priority?: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
  sectorId?: string;
  assignedToId?: string;
  search?: string;
  page?: number;
  limit?: number;
  startDate?: string; // ISO 8601 format
  endDate?: string; // ISO 8601 format
  sortByPriority?: "asc" | "desc";
}

interface GetWorkOrdersResponse {
  data: WorkOrder[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const useGetWorkOrders = (params?: GetWorkOrdersParams) => {
  return useQuery({
    queryKey: ["work-orders", params],
    queryFn: async () => {
      const { data } = await api.get<GetWorkOrdersResponse>("/work-orders", {
        params,
      });
      return data;
    },
  });
};

export const useGetWorkOrderById = (id: string) => {
  return useQuery({
    queryKey: ["work-order", id],
    queryFn: async () => {
      const { data } = await api.get<WorkOrder>(`/work-orders/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

export const useGetWorkOrderHistory = (id: string) => {
  return useQuery({
    queryKey: ["work-order-history", id],
    queryFn: async () => {
      const { data } = await api.get(`/work-orders/${id}/history`);
      return data;
    },
    enabled: !!id,
  });
};

// ============= MUTATIONS =============

interface CreateWorkOrderData {
  title: string;
  description: string;
  priority: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
  sectorId: string;
  assignedToId: string;
  assignedTeamId: string;
  estimatedStartDate: string;
  estimatedEndDate: string;
  occurrenceIds: string[];
}

export const useCreateWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateWorkOrderData) => {
      const response = await api.post("/work-orders", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
    },
  });
};

interface UpdateWorkOrderData {
  title?: string;
  description?: string;
  priority?: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
  sectorId?: string;
  assignedToId?: string;
  estimatedEndDate?: string;
  occurrenceIds?: string[];
  priorityChangeJustification?: string;
}

export const useUpdateWorkOrder = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateWorkOrderData) => {
      const response = await api.patch(`/work-orders/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-order", id] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
    },
  });
};

export const useStartWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/work-orders/${id}/start`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["work-order", id] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["work-order-history", id] });
    },
  });
};

interface EvaluateWorkOrderData {
  completionNotes?: string;
  attachments?: string[];
}

// Hook para AGENTE submeter OS para validação
export const useEvaluateWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data?: EvaluateWorkOrderData;
    }) => {
      const response = await api.post(`/work-orders/${id}/evaluate`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["work-order", id] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["work-order-history", id] });
    },
  });
};

interface CompleteWorkOrderData {
  completionNotes?: string;
  attachments?: string[];
}

// Hook para GERÊNCIA aprovar conclusão (usado apenas por ADMIN/TRIAGE/AUDIT)
export const useCompleteWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data?: CompleteWorkOrderData;
    }) => {
      const response = await api.post(`/work-orders/${id}/complete`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["work-order", id] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["work-order-history", id] });
    },
  });
};

interface SuspendWorkOrderData {
  suspensionReason: string;
}

export const useSuspendWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: SuspendWorkOrderData;
    }) => {
      const response = await api.post(`/work-orders/${id}/suspend`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["work-order", id] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["work-order-history", id] });
    },
  });
};

export const useResumeWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/work-orders/${id}/resume`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["work-order", id] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["work-order-history", id] });
    },
  });
};

interface CancelWorkOrderData {
  cancellationReason: string;
}

export const useCancelWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: CancelWorkOrderData;
    }) => {
      const response = await api.post(`/work-orders/${id}/cancel`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["work-order", id] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["work-order-history", id] });
    },
  });
};

interface ReturnToOuvidoriaData {
  returnReason: string;
}

export const useReturnToOuvidoria = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: ReturnToOuvidoriaData;
    }) => {
      const response = await api.post(
        `/work-orders/${id}/return-to-ouvidoria`,
        data,
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["work-order", id] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["work-order-history", id] });
    },
  });
};
